/**
 * @file active-report.ts
 * @description GCOME_AP 主动上报解析（按协议文档「主动上报数据 / 键盘主动上报」章节）。
 *
 * 设备端主动上报不经 AP 命令应答，而是直接通过 input report 送达，各类型以首字节区分：
 * - 0xA0 按键调试上报（key_debug，20 字节，见 key-debug.ts）
 * - 0xA1 主动上报数据（当前 FN 层 / 配置分组）
 * - 0xFA 0xFB 0x07 电池状态（2.4G 模式插上有线时）
 * - 0xFA 0xFB 0x06 键盘与接收器断连/回连
 * - 0xA3 组合键切换灯光信息上报
 * - 0xA4 摇杆校准上报
 * - 0xC1 驱动存在性探测（键盘按键触发上报 0xC1 0xFF 0xFF 0xFF 0xFF 请求打开驱动；
 *   桌面版驱动后台进程回复 0xC2 0xFF 0xFF 0xFF 0xFF，见 device.ts `replyDriverProbe`。
 *   识图复核 2026-08-15：PDF 原文为 0xC1/0xC2，早期整理误作 0xCF）
 */

import { GcomeKeyDebugUpload, parseGcomeKeyDebug } from './key-debug'

/** 电池状态上报：充电状态。 */
export const GCOME_BATTERY_CHARGE_LABELS: Record<number, string> = {
  0: '未充电',
  1: '充电中',
  2: '快充',
  3: '慢充',
  4: '充电已完成',
}

/** 主动上报统一解析结果（按文档分类）。 */
export type GcomeApActiveReport =
  | { type: 'key-debug'; data: GcomeKeyDebugUpload }
  | { type: 'update'; fnLayer: number; profile: number }
  | {
      type: 'battery'
      chargingCode: number
      chargingLabel: string
      percent: number
    }
  | { type: 'link'; linked: boolean }
  | {
      type: 'lighting'
      profile: number
      backlight: {
        mode: number
        level: number
        speed: number
        trend: number
        egb: number
        color: number
      }
      side: { mode: number; level: number; speed: number; egb: number; color: number }
    }
  | { type: 'joystick-calib' }
  | { type: 'driver-probe' }
  | { type: 'unrecognized' }

/** 设备层 `gcome-ap:active` 事件载荷：解析结果 + 原始字节。 */
export type GcomeApActiveReportEvent = GcomeApActiveReport & { raw: number[] }

/** 解析主动上报载荷（可为 AP 帧 payload 或原始 input report），不匹配时返回 null。 */
export function parseGcomeApActiveReport(input: Uint8Array | number[]): GcomeApActiveReport | null {
  const bytes = Array.from(input)
  if (bytes.length === 0) return null

  switch (bytes[0]) {
    case 0xa0: {
      const data = parseGcomeKeyDebug(bytes)
      return data ? { type: 'key-debug', data } : null
    }
    case 0xa1:
      return { type: 'update', fnLayer: bytes[1] ?? 0, profile: bytes[2] ?? 0 }
    case 0xfa: {
      if (bytes[1] === 0xfb && bytes[2] === 0x07) {
        const chargingCode = bytes[4] ?? 0
        return {
          type: 'battery',
          chargingCode,
          chargingLabel: GCOME_BATTERY_CHARGE_LABELS[chargingCode] ?? `未知(${chargingCode})`,
          percent: bytes[5] ?? 0,
        }
      }
      if (bytes[1] === 0xfb && bytes[2] === 0x06) {
        return { type: 'link', linked: bytes[3] === 0x01 }
      }
      return null
    }
    case 0xa3:
      return {
        type: 'lighting',
        profile: bytes[1] ?? 0,
        backlight: {
          mode: bytes[8] ?? 0,
          level: bytes[9] ?? 0,
          speed: bytes[10] ?? 0,
          trend: bytes[11] ?? 0,
          egb: bytes[12] ?? 0,
          color: bytes[13] ?? 0,
        },
        side: {
          mode: bytes[24] ?? 0,
          level: bytes[25] ?? 0,
          speed: bytes[26] ?? 0,
          egb: bytes[27] ?? 0,
          color: bytes[28] ?? 0,
        },
      }
    case 0xa4:
      return { type: 'joystick-calib' }
    case 0xc1:
      return { type: 'driver-probe' }
    default:
      return null
  }
}

/** 上报类型文案（展示用）。 */
export const GCOME_ACTIVE_REPORT_LABELS: Record<GcomeApActiveReport['type'], string> = {
  'key-debug': '0xA0 按键调试',
  update: '0xA1 主动上报数据',
  battery: '0xFA 0xFB 0x07 电池状态',
  link: '0xFA 0xFB 0x06 断连/回连',
  lighting: '0xA3 灯光信息',
  'joystick-calib': '0xA4 摇杆校准',
  'driver-probe': '0xC1 驱动探测',
  unrecognized: '未识别',
}
